import * as React from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Button } from './button';
import { Badge } from './badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from 'ui/command';

export interface MultiSelectOption {
  label: string;
  value: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  className?: string;
  closeOnSelect?: boolean;
  disabled?: boolean;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = 'Select items...',
  className = '',
  closeOnSelect = false, 
  disabled = false,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
  if (!open) {
    return;
  }

  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as Node;

    if (
      containerRef.current &&
      !containerRef.current.contains(target)
    ) {
      setOpen(false);
    }
  };

  document.addEventListener("mousedown", handleOutsideClick);

  return () => {
    document.removeEventListener("mousedown", handleOutsideClick);
  };
}, [open]);

  const handleSelect = (value: string) => {
    let updated: string[];

    if (selected.includes(value)) {
      updated = selected.filter((item) => item !== value);
    } else {
      updated = [...selected, value];
    }

    onChange(updated);

    if (closeOnSelect) {
      setOpen(false); 
    }
  };

  const handleRemove = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter((item) => item !== value));
  };

  return (
  <div
    ref={containerRef}
    className="relative w-full"
  >
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      disabled={disabled}
      onClick={() => {
        if (!disabled) {
          setOpen((prev) => !prev);
        }
      }}
      className={`w-full justify-between h-auto min-h-[36px] ${className}`}
    >
      <div className="flex flex-wrap gap-1 flex-1 text-left">
        {selected.length === 0 ? (
          <span className="text-muted-foreground text-sm">
            {placeholder}
          </span>
        ) : (
          selected.map((value) => {
            const option = options.find(
              (opt) => opt.value === value
            );

            return (
              <Badge
                key={value}
                variant="secondary"
                className="mr-1 text-xs"
                style={{
                  backgroundColor: "#5B9BD5",
                  color: "white",
                }}
              >
                {option?.label}

                <span
                  className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onKeyDown={(e) => {
                    if (!disabled && e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRemove(value, e as unknown as React.MouseEvent);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (!disabled) {
                      handleRemove(value, e);
                    }
                  }}
                >
                  <X className="h-3 w-3 text-white hover:text-gray-200" />
                </span>
              </Badge>
            );
          })
        )}
      </div>

      <ChevronDown
        className={`h-4 w-4 shrink-0 opacity-50 ml-2 transition-transform ${
          open ? "rotate-180" : ""
        }`}
      />
    </Button>

    {open && !disabled && (
      <div 
        className="absolute left-0 bottom-full mb-1 w-full max-h-64 rounded-md border bg-white shadow-md z-[100]" 
        onMouseDown={(e) => { 
          e.stopPropagation(); 
        }} 
      >
        <Command>
          <CommandInput
            placeholder="Search..."
            className="h-9 focus:outline-none focus:ring-0 focus:border-transparent"
          />

          <CommandEmpty>
            No item found.
          </CommandEmpty>

          <CommandGroup className="max-h-64 overflow-auto">
            {options.map((option) => {
              const isSelected = selected.includes(option.value);

              return (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    handleSelect(option.value);
                  }}
                  className="cursor-pointer"
                >
                  <div
                    className={`mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                      isSelected
                        ? "text-white"
                        : "border-gray-300 [&_svg]:invisible"
                    }`}
                    style={
                      isSelected
                        ? {
                            backgroundColor: "#5B9BD5",
                            borderColor: "#5B9BD5",
                          }
                        : {}
                    }
                  >
                    <Check className="h-4 w-4" />
                  </div>

                  <span>{option.label}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </Command>
      </div>
    )}
  </div>
);
}
